import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { TApi, TicketTypeOptions } from '../../../../shared/const'
import http from '../../../../shared/http'
import { notifyError } from '../../../../shared/errorMessage'
import useDialogDismiss from '../../../../shared/useDialogDismiss'
import useIsDirty from '../../../../shared/useIsDirty'
import { askDiscardChanges } from '../../../../shared/confirmDialog'
import { joinEffort, splitEffort } from '../../../../shared/effort'

import Btn from '../../../../UI/Btn'
import { PlainCheckbox, PlainInput, PlainSelect, PlainTextarea } from '../../../../UI/PlainField'

import styles from './edit.module.scss'
import { formatDateTime } from '../../../../shared/datetime'

/**
 * Beschreibung eines Eintrags nachbessern.
 *
 * Änderbar sind nur die Texte, die Einordnung und der Aufwand. Datum, Herkunft und Kennung
 * bleiben, wie sie aus der Versionsverwaltung kamen — sonst taugt die Liste
 * nicht mehr als Nachweis.
 *
 * Wer hier etwas ändert, schützt den Eintrag damit dauerhaft: Das nächtliche
 * Nachziehen der Git-Daten lässt bearbeitete Zeilen unangetastet.
 */
export default function EntryEditModal({ entry, projects, onClose, onSaved }) {
	const [form, setForm] = useState({
		title: entry.title || '',
		summary: entry.summary || '',
		category: entry.category || '',
		area: entry.area || '',
		userFacing: Boolean(entry.userFacing),
		projectId: entry.projectId ? String(entry.projectId) : '',
		effortHours: splitEffort(entry.effortMinutes).hours,
		effortMinutes: splitEffort(entry.effortMinutes).minutes,
		ticketId: entry.ticketId ? String(entry.ticketId) : '',
		type: entry.type || ''
	})
	// Die gefundene Anforderung zur eingetippten Nummer: null = noch nichts
	// gesucht, false = gibt es nicht, sonst { id, title, … }.
	const [ticket, setTicket] = useState(entry.ticket || null)
	const [isSaving, setSaving] = useState(false)

	const isDirty = useIsDirty(form)

	const schliessen = async () => {
		if (isDirty && !(await askDiscardChanges())) return

		onClose()
	}

	const backdropProps = useDialogDismiss(schliessen, { enabled: !isSaving })

	const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))

	const effortMinutes = joinEffort({ hours: form.effortHours, minutes: form.effortMinutes })
	const effortInvalid = effortMinutes === undefined

	const ticketNumber = form.ticketId.trim().replace(/^#/, '')
	const ticketChanged = ticketNumber !== (entry.ticketId ? String(entry.ticketId) : '')
	const ticketInvalid = Boolean(ticketNumber) && (!/^\d+$/.test(ticketNumber) || ticket === false)

	// Zur eingetippten Nummer den Titel zeigen — man soll sehen, womit man
	// verknüpft, bevor man speichert. Leicht verzögert, nicht bei jedem Zeichen.
	useEffect(() => {
		if (!ticketChanged) return undefined
		if (!/^\d+$/.test(ticketNumber)) {
			setTicket(null)
			return undefined
		}
		let aktuell = true
		const timer = setTimeout(() => {
			http.get(TApi.ADMIN_TICKET_ID(ticketNumber), { disableToast: true })
				.then(({ data }) => aktuell && setTicket(data))
				.catch(() => aktuell && setTicket(false))
		}, 300)
		return () => {
			aktuell = false
			clearTimeout(timer)
		}
	}, [ticketNumber, ticketChanged])

	/*
	 * Beim Verknüpfen übernimmt der Server Projekt und Art der Anforderung.
	 * Das Fenster schickt beide deshalb nur mit, wenn sie hier ausdrücklich
	 * geändert wurden — sonst gewänne der alte Wert aus dem Formular.
	 */
	const projectChanged = form.projectId !== (entry.projectId ? String(entry.projectId) : '')
	const typeChanged = form.type !== (entry.type || '')

	const submit = event => {
		event.preventDefault()
		if (form.title.trim().length < 3 || effortInvalid || ticketInvalid) return

		const assignment = { effortMinutes }
		if (ticketChanged) assignment.ticketId = ticketNumber ? Number(ticketNumber) : null
		if (!ticketChanged || !ticketNumber || projectChanged) {
			assignment.projectId = form.projectId ? Number(form.projectId) : null
		}
		if (!ticketChanged || !ticketNumber || typeChanged) assignment.type = form.type || null

		setSaving(true)
		// `disableToast`: Der eigene Satz sagt genauer, was nicht geklappt hat.
		http.put(
			TApi.ADMIN_CHANGELOG_ID(entry.id),
			{
				title: form.title.trim(),
				summary: form.summary.trim() || null,
				category: form.category.trim() || null,
				area: form.area.trim() || null,
				userFacing: form.userFacing,
				...assignment
			},
			{ disableToast: true }
		)
			.then(() => {
				toast.success('Eintrag gespeichert.')
				onSaved()
			})
			.catch(error => notifyError(error, 'Speichern fehlgeschlagen.'))
			.finally(() => setSaving(false))
	}

	return (
		<div className={styles.backdrop} {...backdropProps}>
			<form className={styles.modal} onSubmit={submit}>
				<header className={styles.head}>
					<h2 className={styles.title}>Eintrag bearbeiten</h2>
					<button type="button" className={styles.close} onClick={schliessen} aria-label="Schließen">
						×
					</button>
				</header>

				<div className={styles.body}>
					<PlainInput
						label="Titel"
						value={form.title}
						maxLength={255}
						autoFocus
						onChange={event => set('title', event.target.value)}
					/>
					<PlainTextarea
						label="Beschreibung"
						rows={5}
						value={form.summary}
						maxLength={5000}
						onChange={event => set('summary', event.target.value)}
					/>
					<PlainSelect
						label="Projekt"
						value={form.projectId}
						placeholder="Ohne Projekt"
						options={projects.map(project => ({
							value: String(project.id),
							label: project.title
						}))}
						onChange={event => set('projectId', event.target.value)}
					/>

					{/*
					 * Anforderung und Art: Verknüpfen übernimmt Projekt und Art
					 * der Anforderung — das sagt der Hinweis, bevor man speichert.
					 */}
					<div className={styles.grid}>
						<PlainInput
							label="Anforderung (Nummer)"
							value={form.ticketId}
							inputMode="numeric"
							placeholder="z. B. 184"
							error={ticketInvalid ? 'Diese Anforderung gibt es nicht.' : undefined}
							onChange={event => set('ticketId', event.target.value)}
						/>
						<PlainSelect
							label="Art"
							value={form.type}
							placeholder="Ohne Art"
							options={TicketTypeOptions}
							onChange={event => set('type', event.target.value)}
						/>
					</div>
					{ticketNumber && ticket && (
						<p className={styles.ticketHint}>
							#{ticket.id} {ticket.title}
							{ticketChanged &&
								(!projectChanged || !typeChanged) &&
								' — Projekt und Art werden aus der Anforderung übernommen.'}
						</p>
					)}

					<div className={styles.grid}>
						<PlainInput
							label="Einordnung"
							value={form.category}
							maxLength={100}
							onChange={event => set('category', event.target.value)}
						/>
						<PlainInput
							label="Bereich"
							value={form.area}
							maxLength={100}
							onChange={event => set('area', event.target.value)}
						/>
					</div>
					{/*
					 * Stunden und Minuten getrennt: „1,25" liest niemand als
					 * eine Stunde und eine Viertelstunde. Beide leer heißt
					 * „nicht geschätzt" — nicht null Minuten.
					 */}
					<div className={styles.grid}>
						<PlainInput
							label="Aufwand — Stunden"
							type="number"
							min={0}
							step={1}
							inputMode="numeric"
							value={form.effortHours}
							onChange={event => set('effortHours', event.target.value)}
						/>
						<PlainInput
							label="Aufwand — Minuten"
							type="number"
							min={0}
							step={5}
							inputMode="numeric"
							value={form.effortMinutes}
							error={effortInvalid ? 'Bitte ganze Zahlen ab 0 eintragen.' : undefined}
							onChange={event => set('effortMinutes', event.target.value)}
						/>
					</div>
					<PlainCheckbox
						label="Für Anwender:innen sichtbare Änderung"
						checked={form.userFacing}
						onChange={event => set('userFacing', event.target.checked)}
					/>

					<div className={styles.source}>
						<span className={styles.sourceLabel}>Herkunft (unveränderlich)</span>
						<span className={styles.sourceValue}>
							{entry.repo} · {entry.commitSha} · {formatDateTime(entry.occurredAt)}
						</span>
					</div>

					{!entry.isEdited && (
						<p className={styles.hint}>
							Nach dem Speichern wird dieser Eintrag beim automatischen Nachziehen der Änderungen nicht
							mehr überschrieben.
						</p>
					)}
				</div>

				<footer className={styles.foot}>
					<Btn type="button" theme="secondary" onClick={schliessen}>
						Abbrechen
					</Btn>
					<Btn type="submit" theme="primary" disabled={isSaving || effortInvalid}>
						{isSaving ? 'Wird gespeichert …' : 'Speichern'}
					</Btn>
				</footer>
			</form>
		</div>
	)
}

EntryEditModal.propTypes = {
	entry: PropTypes.object.isRequired,
	// Projekte der Anforderungsverwaltung — dieselbe Liste wie im Filter.
	projects: PropTypes.array,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired
}

EntryEditModal.defaultProps = {
	projects: []
}
